import {
  KeyboardCode,
  type KeyboardCoordinateGetter,
  PointerSensor,
  type PointerSensorOptions,
} from '@dnd-kit/core';
import type { Immutable } from 'immer';
import type { PointerEvent } from 'react';

/** What a drag carries. dnd-kit hands this back untyped, so it is narrowed before use. */
export type DragData =
  | { type: 'total-trackers' | 'sample-trackers'; ids: Immutable<Set<string>> }
  | { type: 'filter-tree'; key: string; count: number };

export type DragType = DragData['type'];

const DRAG_TYPES: ReadonlyArray<string> = ['total-trackers', 'sample-trackers', 'filter-tree'];

const isDragData = (data: unknown): data is DragData => {
  if (typeof data !== 'object' || data === null || !('type' in data)) return false;
  return typeof data.type === 'string' && DRAG_TYPES.includes(data.type);
};

/**
 * Whether a zone will take the current drag.
 *
 * `useDroppable` has no `accept` of its own and reports `isOver` for every drag, so the type
 * check react-dnd used to perform has to happen here.
 */
export const isAccepted = (data: unknown, accept: ReadonlyArray<string>): boolean =>
  isDragData(data) && accept.includes(data.type);

export interface Coordinates {
  x: number;
  y: number;
}

export interface DroppableTarget {
  accept: ReadonlyArray<string>;
  rect: { top: number; left: number; width: number; height: number };
}

type Direction = 'up' | 'down' | 'left' | 'right';

const isAhead = (direction: Direction, from: Coordinates, to: Coordinates): boolean => {
  switch (direction) {
    case 'up':
      return to.y < from.y;
    case 'down':
      return to.y > from.y;
    case 'left':
      return to.x < from.x;
    case 'right':
      return to.x > from.x;
  }
};

/**
 * Where an arrow key should move a keyboard drag: the centre of the nearest zone that would
 * accept it, in the pressed direction, or null when there is nothing that way.
 *
 * The stock KeyboardSensor steps by 25px, which is fine for a sortable list but takes dozens of
 * presses to cross a splitter panel — and gives no hint which zones are live.
 */
export const nextDroppableCoordinates = ({
  direction,
  current,
  activeType,
  targets,
}: {
  direction: Direction;
  current: Coordinates;
  activeType: string;
  targets: ReadonlyArray<DroppableTarget>;
}): Coordinates | null => {
  const candidates = targets
    .filter((t) => t.accept.includes(activeType))
    .map(({ rect }) => ({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }))
    .filter((centre) => isAhead(direction, current, centre));

  if (candidates.length === 0) return null;

  return candidates.reduce((nearest, centre) =>
    Math.hypot(centre.x - current.x, centre.y - current.y) <
    Math.hypot(nearest.x - current.x, nearest.y - current.y)
      ? centre
      : nearest,
  );
};

const INTERACTIVE_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button',
  'a',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '.ant-checkbox',
  '.ant-select',
  '.ant-picker',
].join(',');

/**
 * Whether a pointerdown should be left alone rather than allowed to start a drag.
 *
 * react-dnd rode on the native `draggable` attribute, which the browser already suppressed inside
 * form controls. dnd-kit claims `pointerdown` outright, so the exemption is spelled out here —
 * otherwise ticking a row checkbox or opening a tag editor would start a drag.
 *
 * Two things are exempt. Anything interactive inside the source; and anything outside the source
 * altogether, which means antd rendered it through a portal — a tooltip or dropdown raised from a
 * cell sits under document.body, yet React still bubbles its events to the row. Position is the
 * only signal there, since such content is often a bare styled icon matching no selector.
 *
 * The source itself never counts as interactive: dnd-kit labels every one `role="button"`, which
 * would otherwise match and block every drag.
 */
export const shouldIgnoreDragStart = (
  target: EventTarget | null,
  draggable?: Element | null,
): boolean => {
  if (!(target instanceof Element)) return false;
  if (draggable != null && !draggable.contains(target)) return true;

  const match = target.closest(INTERACTIVE_SELECTOR);
  return match != null && match !== draggable;
};

/** PointerSensor that leaves interactive and portaled cell content alone. Otherwise stock. */
export class RowPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (syntheticEvent: PointerEvent, { onActivation }: PointerSensorOptions) => {
        const event = syntheticEvent.nativeEvent;
        if (!event.isPrimary || event.button !== 0) return false;
        // currentTarget is the element the listeners were spread on — the drag source itself.
        if (shouldIgnoreDragStart(event.target, syntheticEvent.currentTarget)) return false;

        onActivation?.({ event });
        return true;
      },
    },
  ];
}

const DIRECTION_BY_CODE: Readonly<Record<string, Direction>> = {
  [KeyboardCode.Up]: 'up',
  [KeyboardCode.Down]: 'down',
  [KeyboardCode.Left]: 'left',
  [KeyboardCode.Right]: 'right',
};

/**
 * Adapts dnd-kit's keyboard context onto `nextDroppableCoordinates`.
 *
 * dnd-kit measures from the dragged rect's top-left, while the jump is easier to reason about
 * between centres, so the half-size is added on the way in and taken off on the way out.
 */
export const arrowKeyCoordinateGetter: KeyboardCoordinateGetter = (
  event,
  { currentCoordinates, context },
) => {
  const direction = DIRECTION_BY_CODE[event.code];
  if (direction == null) return undefined;
  event.preventDefault();

  const activeData = context.active?.data.current;
  if (!isDragData(activeData)) return undefined;

  const half = {
    x: (context.collisionRect?.width ?? 0) / 2,
    y: (context.collisionRect?.height ?? 0) / 2,
  };

  const targets: Array<DroppableTarget> = [];
  for (const container of context.droppableContainers.getEnabled()) {
    const rect = context.droppableRects.get(container.id) ?? container.rect.current;
    const accept = container.data.current?.accept;
    if (rect == null || !Array.isArray(accept)) continue;
    targets.push({ accept, rect });
  }

  const next = nextDroppableCoordinates({
    direction,
    current: { x: currentCoordinates.x + half.x, y: currentCoordinates.y + half.y },
    activeType: activeData.type,
    targets,
  });

  return next == null ? undefined : { x: next.x - half.x, y: next.y - half.y };
};

export interface TrackerDropItem {
  itemType: 'total-trackers' | 'sample-trackers';
  data: { ids: Immutable<Set<string>> };
}

export interface FilterDropItem {
  itemType: 'filter-tree';
  data: { key: string; count: number };
}

/** Turns a drag payload into the item shape the drop handlers expect, or null if it is unusable. */
export const toDropItem = (data: unknown): TrackerDropItem | FilterDropItem | null => {
  if (!isDragData(data)) return null;

  if (data.type === 'filter-tree') {
    return data.key != null && data.count != null
      ? { itemType: 'filter-tree', data: { key: data.key, count: data.count } }
      : null;
  }

  return data.ids != null ? { itemType: data.type, data: { ids: data.ids } } : null;
};

/**
 * Which rows travel with a drag. Grabbing a row inside the current selection moves the whole
 * selection; grabbing one outside it moves just that row.
 */
export const getDragIds = (
  rowKey: string,
  selectionIds?: Immutable<Set<string>>,
): Immutable<Set<string>> => (selectionIds?.has(rowKey) ? selectionIds : new Set([rowKey]));
